import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { blogApi, type BlogPostSummary } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

const HOME_BLOG_LIMIT = 4;

const useFormatDate = () => {
    const { lang } = useLanguage();
    return (iso?: string) => {
        if (!iso) return "";
        try {
            return new Intl.DateTimeFormat(lang, { year: "numeric", month: "short", day: "numeric" }).format(
                new Date(iso),
            );
        } catch {
            return "";
        }
    };
};

const FeaturedRow = ({ post, formatDate }: { post: BlogPostSummary; formatDate: (iso?: string) => string }) => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const categoryName = post.category?.name ?? t("blog_home.default_category");

    return (
        <Link
            to={localizedPath(`/blog/${post.slug}`)}
            data-cursor="hover"
            className="group block relative border-b border-white/10 first:border-t hover:bg-white/[0.025] transition-colors duration-300"
        >
            <div className="container mx-auto px-5 sm:px-6">
                <div className="grid grid-cols-12 items-center gap-3 sm:gap-6 py-6 sm:py-7">
                    <span className="col-span-2 sm:col-span-1 font-mono-brutal text-[11px] text-white/40 tabular-nums">
                        [01]
                    </span>

                    {post.featuredImage ? (
                        <div className="hidden sm:block sm:col-span-2 md:col-span-2 aspect-[4/3] overflow-hidden border border-white/10">
                            <img
                                src={post.featuredImage}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                            />
                        </div>
                    ) : (
                        <div aria-hidden className="hidden sm:block sm:col-span-2 md:col-span-2" />
                    )}

                    <div className="col-span-7 sm:col-span-6 md:col-span-7 min-w-0">
                        <span className="font-mono-brutal text-[9px] text-white/45 mb-2 inline-block">
                            {categoryName} · {post.readTime} {t("blog_home.read_time_suffix")}
                        </span>
                        <h3 className="font-v2-display text-xl sm:text-2xl md:text-3xl text-white leading-snug line-clamp-2">
                            {post.title}
                        </h3>
                    </div>

                    <div className="col-span-3 sm:col-span-3 md:col-span-2 flex items-center justify-end gap-3">
                        <span className="hidden md:inline font-mono-brutal text-[10px] text-white/40 tabular-nums whitespace-nowrap">
                            {formatDate(post.publishedAt)}
                        </span>
                        <span
                            className="inline-flex items-center justify-center w-9 h-9 border border-white/20 transition-all duration-300"
                            style={{
                                background:
                                    "linear-gradient(transparent, transparent), var(--gradient-accent-v2)",
                                backgroundClip: "padding-box, border-box",
                            }}
                        >
                            <ArrowUpRight
                                size={14}
                                className="text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                            />
                        </span>
                    </div>
                </div>
            </div>

            <span
                aria-hidden
                className="absolute bottom-0 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 origin-start transition-transform duration-500"
                style={{ background: "var(--gradient-accent-v2)" }}
            />
        </Link>
    );
};

const PostRow = ({
    post,
    index,
    formatDate,
}: {
    post: BlogPostSummary;
    index: number;
    formatDate: (iso?: string) => string;
}) => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const categoryName = post.category?.name ?? t("blog_home.default_category");
    const num = String(index + 1).padStart(2, "0");

    return (
        <Link
            to={localizedPath(`/blog/${post.slug}`)}
            data-cursor="hover"
            className="group block relative border-b border-white/10 hover:bg-white/[0.025] transition-colors duration-300"
        >
            <div className="container mx-auto px-5 sm:px-6">
                <div className="grid grid-cols-12 items-center gap-3 sm:gap-6 py-5 sm:py-6">
                    <span className="col-span-2 sm:col-span-1 font-mono-brutal text-[11px] text-white/40 tabular-nums">
                        [{num}]
                    </span>

                    <div className="col-span-7 sm:col-span-8 min-w-0">
                        <h4 className="font-v2-display text-base sm:text-lg md:text-xl text-white leading-snug line-clamp-1">
                            {post.title}
                        </h4>
                        <p className="font-mono-brutal text-[9px] text-white/45 mt-1.5">
                            {categoryName} · {post.readTime} {t("blog_home.read_time_suffix")}
                        </p>
                    </div>

                    <div className="col-span-3 sm:col-span-3 flex items-center justify-end gap-3">
                        <span className="hidden md:inline font-mono-brutal text-[10px] text-white/40 tabular-nums whitespace-nowrap">
                            {formatDate(post.publishedAt)}
                        </span>
                        <ArrowUpRight
                            size={14}
                            className="text-white/55 transition-all duration-300 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                    </div>
                </div>
            </div>

            <span
                aria-hidden
                className="absolute bottom-0 inset-x-0 h-px scale-x-0 group-hover:scale-x-100 origin-start transition-transform duration-500"
                style={{ background: "var(--gradient-accent-v2)" }}
            />
        </Link>
    );
};

const BlogTimeline = () => {
    const { t } = useTranslation();
    const { lang } = useLanguage();
    const localizedPath = useLocalizedPath();
    const formatDate = useFormatDate();

    const { data, isLoading, isError } = useQuery({
        queryKey: ["home-blog", HOME_BLOG_LIMIT, lang],
        queryFn: async () => {
            const res = await blogApi.getPosts({ limit: HOME_BLOG_LIMIT, page: 1, lang });
            return res.data.data;
        },
        staleTime: 15 * 60 * 1000,
        retry: 1,
    });

    const featured = data?.featuredPost ?? data?.posts?.[0];
    const rest = (data?.posts ?? []).filter((p) => p.id !== featured?.id).slice(0, HOME_BLOG_LIMIT - 1);

    if (!isLoading && (isError || !featured)) {
        return null;
    }

    return (
        <section
            id="blog"
            className="py-12 sm:py-16 md:py-24 lg:py-28"
            aria-labelledby="blog-heading"
        >
            <div className="container mx-auto px-5 sm:px-6 mb-10 sm:mb-14">
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                    <span aria-hidden className="brutal-glyph" style={{ height: "0.65em" }} />
                    <span className="font-mono-brutal text-[11px] text-white/60">
                        {t("blog_home.eyebrow")}
                    </span>
                    <span aria-hidden className="flex-1 h-px bg-white/10" />
                    <Link
                        to={localizedPath("/blog")}
                        data-cursor="hover"
                        className="font-mono-brutal text-[10px] text-white/55 hover:text-white transition-colors flex items-center gap-1.5"
                    >
                        {t("blog_home.see_all")}
                        <ArrowUpRight size={12} />
                    </Link>
                </div>

                <h2
                    id="blog-heading"
                    className="font-v2-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl"
                >
                    {t("blog_home.title_prefix")}{" "}
                    <span
                        style={{
                            backgroundImage: "var(--gradient-accent-v2)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            color: "transparent",
                        }}
                    >
                        {t("blog_home.title_highlight")}
                    </span>
                </h2>
            </div>

            {isLoading ? (
                <div className="container mx-auto px-5 sm:px-6">
                    <div className="border-y border-white/10 py-6 animate-pulse">
                        <div className="h-6 w-2/3 bg-white/5" />
                    </div>
                </div>
            ) : (
                <div className="relative">
                    {featured && <FeaturedRow post={featured} formatDate={formatDate} />}
                    {rest.map((post, i) => (
                        <PostRow key={post.id} post={post} index={i + 1} formatDate={formatDate} />
                    ))}
                </div>
            )}
        </section>
    );
};

export default BlogTimeline;
